// StepIsland — the "Dynamic Island" workflow stepper (BO UI Standard §5).
// The ONE stepper for Class C (transaction) / Class D (workflow-review) forms:
// a black pill docked in the top banner. Props-only — no data awareness; the
// host form decides each step's state (an OPTIONAL step becomes 'done' only
// when its action actually happens, never on tab-open).
import './stepIsland.css';

export type StepState = 'done' | 'active' | 'optional' | 'todo';

export interface IslandStep {
  id: string;
  label: string;
  state: StepState;
}

export interface StepIslandProps {
  steps: IslandStep[];
  /** Optional step click (e.g. jump back to a completed step). Only steps the
   *  host allows are clickable — omit to render a purely visual stepper. */
  onStepClick?: (id: string) => void;
  /** Which steps may be clicked when onStepClick is provided (default: done). */
  isClickable?: (step: IslandStep) => boolean;
  className?: string;
}

/** The shared Dynamic Island stepper. Renders steps left → right with glowing
 *  connectors; a connector "lights up" green once the step before it is done. */
export function StepIsland({ steps, onStepClick, isClickable, className }: StepIslandProps) {
  const canClick = (s: IslandStep) =>
    !!onStepClick && (isClickable ? isClickable(s) : s.state === 'done');

  return (
    <div className={`step-island${className ? ` ${className}` : ''}`} role="list" aria-label="Workflow steps">
      {steps.map((s, i) => (
        <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 && <span className={`si-conn${steps[i - 1]!.state === 'done' ? ' si-conn-done' : ''}`} />}
          <button
            type="button"
            role="listitem"
            className={`si-step si-${s.state}${canClick(s) ? ' si-clickable' : ''}`}
            onClick={canClick(s) ? () => onStepClick!(s.id) : undefined}
            disabled={!canClick(s)}
            aria-current={s.state === 'active' ? 'step' : undefined}
            title={s.label}
          >
            <span className="si-circle">{s.state === 'done' ? '✓' : i + 1}</span>
            <span className="si-label">{s.label}</span>
          </button>
        </span>
      ))}
    </div>
  );
}
